import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL_SERVER } from '../../api/api';

const TradeDashboard = ({ accountId, strategy }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [positions, setPositions] = useState([]);
    const [margin, setMargin] = useState(0); // State for dynamic margin

    // Fetch positions and margin every second
    useEffect(() => {
        const fetchPositions = async () => {
            try {
                const response = await axios.get(`${API_URL_SERVER}/positions`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                const { positions: allPositions, account_margin } = response.data;
                // Filter positions by accountId and strategy
                const filteredPositions = allPositions.filter(
                    pos => pos.accountId === accountId && pos.strategy === strategy
                );
                // Map positions to table format
                const mappedPositions = filteredPositions.map((pos, index) => ({
                    id: pos.ticket || index + 1,
                    symbol: pos.symbol,
                    qty: pos.volume, // Adjust as needed
                    buyTime: pos.time,
                    buyPrice: pos.price,
                    sellPrice: pos.currentPrice,
                    sellTime: '-',
                    target1: pos.tp || '-',
                    target2: '-',
                    stoploss: pos.sl || '-',
                    ltp: pos.currentPrice,
                    pnl: pos.profit,
                    status: 'OPEN',
                    target1Status: pos.tp ? 'normal' : 'normal',
                    target2Status: 'normal',
                    stoplossStatus: pos.sl ? 'normal' : 'normal',
                }));
                setPositions(mappedPositions);
                // Set margin from account_margin
                const marginKey = `${accountId}:${strategy}`;
                setMargin(account_margin[marginKey] || 0);
            } catch (error) {
                console.error('Fetch positions error:', error);
                setPositions([]);
                setMargin(0);
            }
        };

        fetchPositions();
        const interval = setInterval(fetchPositions, 1000);
        return () => clearInterval(interval);
    }, [accountId, strategy]);

    // Filter positions based on search term
    const filteredOrders = positions.filter(order =>
        order.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Calculate totals
    const totalPnL = filteredOrders.reduce((total, order) => total + order.pnl, 0);
    const totalAmount = margin + totalPnL; // Use dynamic margin
    const returnPercentage = margin ? ((totalPnL / margin) * 100).toFixed(2) : 0;

    return (
        <div className="order-history-container">
            <div className="order-history-header">
                <h2>Live Trades for {strategy}</h2>
                <div className="reports-link">
                    <Link to="/reports">Reports</Link>
                </div>
            </div>

            <div className="search-container">
                <label htmlFor="search">Search: </label>
                <input
                    type="text"
                    id="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                />
            </div>

            <div className="table-responsive">
                {filteredOrders.length === 0 ? (
                    <p>No open positions for this account.</p>
                ) : (
                    <table className="table order-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Symbol</th>
                                <th>Lot Size</th>
                                <th>Buy Time</th>
                                <th>Buy Price</th>
                                <th>Sell Price</th>
                                <th>Sell Time</th>
                                <th>Target</th>
                                <th>Stoploss</th>
                                <th>LTP</th>
                                <th>P&L</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map((order) => (
                                <tr key={order.id} className={order.pnl >= 0 ? 'positive-row' : 'negative-row'}>
                                    <td>{order.id}</td>
                                    <td>{order.symbol}</td>
                                    <td>{order.qty.toFixed(2)}</td>
                                    <td>{order.buyTime}</td>
                                    <td>{order.buyPrice.toFixed(2)}</td>
                                    <td>{order.sellPrice.toFixed(2)}</td>
                                    <td>{order.sellTime}</td>
                                    <td className={`target-cell ${order.target1Status}`}>
                                        {order.target1}
                                        <div className="edit-link">Edit TP</div>
                                    </td>
                                    <td className={`stoploss-cell ${order.stoplossStatus}`}>
                                        {order.stoploss}
                                        <div className="edit-link">Edit SL</div>
                                    </td>
                                    <td className={order.pnl >= 0 ? 'positive-value' : 'negative-value'}>
                                        {order.ltp.toFixed(2)}
                                    </td>
                                    <td className={order.pnl >= 0 ? 'positive-value' : 'negative-value'}>
                                        {order.pnl.toFixed(1)}
                                    </td>
                                    <td className="status-cell">{order.status}</td>
                                    <td>
                                        <button className="exit-btn">EXIT</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                <div className="d-flex gap-5">
                    <div className="d-flex align-items-center">
                        <h3>Margin</h3>
                        <p className="mb-0 ps-2">${margin.toLocaleString()}</p>
                    </div>
                    <div className="d-flex align-items-center">
                        <h3 className={totalPnL >= 0 ? 'positive-value' : 'negative-value'}>Total PnL</h3>
                        <p className={totalPnL >= 0 ? 'positive-value mb-0 ps-2' : 'negative-value mb-0 ps-2'}>
                            ${totalPnL.toFixed(0)}
                            <span className="return-percentage">({returnPercentage}%)</span>
                            ${totalAmount.toLocaleString()}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TradeDashboard;